import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, collectionGroup } from 'firebase/firestore';
import { db } from '../firebase/config'; // Adjust path as needed

export function usePendingTasks(appId, userProfile) {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!appId || !userProfile?.id) {
            setLoading(false);
            return;
        }

        const uid = userProfile.id;
        const isIT = userProfile.department === 'IT';
        const isHR = userProfile.department === 'HR';
        const isFinanceApprover = userProfile.department === 'Finance' && userProfile.role === 'Approver';

        // Array to hold unsubscribe functions so we can clean them up
        const unsubscribes = [];

        // Temporary storage for our streams
        const taskStreams = {
            pr: [], invoices: [], expenses: [], office: [], it: [], perf: [], pd: [], users: [], exits: []
        };

        const updateTasks = () => {
            const allTasks = Object.values(taskStreams).flat();
            // Sort newest first
            allTasks.sort((a, b) => b.date - a.date);
            setTasks(allTasks);
        };

        // 1. Purchase Requests (Pending Manager Approval)
        const qPR = query(
            collection(db, `artifacts/${appId}/public/data/purchaseRequests`),
            where('assignedApproverId', '==', uid),
            where('status', '==', 'Pending Manager Approval')
        );
        unsubscribes.push(onSnapshot(qPR, (snap) => {
            taskStreams.pr = snap.docs.map(doc => {
                const d = doc.data();
                return {
                    id: doc.id, type: 'procurement', module: 'purchase_request',
                    title: `PR Approval: ${d.requestNumber}`,
                    description: `${d.requesterName} requests ${d.currency} ${d.approxValue}`,
                    date: d.createdAt?.toDate() || new Date(),
                    route: 'procurement' // Adjust routes based on your app structure
                };
            });
            updateTasks();
        }));

        // 2. Invoices (Using Collection Group query to catch all invoices across all PRs)
        const qInvoices = query(
            collectionGroup(db, 'invoices'),
            where('assignedApproverId', '==', uid),
            where('status', '==', 'Pending Invoice Approval')
        );
        unsubscribes.push(onSnapshot(qInvoices, (snap) => {
            // Filter locally to ensure they belong to this appId
            const validDocs = snap.docs.filter(d => d.ref.path.includes(appId));
            taskStreams.invoices = validDocs.map(doc => {
                const d = doc.data();
                return {
                    id: doc.id, type: 'procurement', module: 'invoice',
                    title: `Invoice Approval: ${d.invoiceNumber}`,
                    description: `${d.submittedBy} submitted invoice for ${d.currency} ${d.amount}`,
                    date: d.createdAt?.toDate() || new Date(),
                    route: 'procurement'
                };
            });
            updateTasks();
        }));

        // 3. Expense Reports
        const qExpenses = query(
            collection(db, `artifacts/${appId}/public/data/expenseReports`),
            where('managerId', '==', uid),
            where('status', '==', 'Pending Approval')
        );
        unsubscribes.push(onSnapshot(qExpenses, (snap) => {
            taskStreams.expenses = snap.docs.map(doc => {
                const d = doc.data();
                return {
                    id: doc.id, type: 'expenses', module: 'expense',
                    title: `Expense Approval: ${d.reportNumber}`,
                    description: `${d.requesterName} submitted ${d.reimbursementCurrency} ${d.totalAmount}`,
                    date: d.createdAt?.toDate() || new Date(),
                    route: 'expenses'
                };
            });
            updateTasks();
        }));

        // 4. Office Scheduler Deviations
        const qOffice = query(
            collection(db, `artifacts/${appId}/schedule_deviations`),
            where('managerId', '==', uid),
            where('status', '==', 'Pending')
        );
        unsubscribes.push(onSnapshot(qOffice, (snap) => {
            taskStreams.office = snap.docs.map(doc => {
                const d = doc.data();
                return {
                    id: doc.id, type: 'office', module: 'office',
                    title: `Schedule Request`,
                    description: `${d.userName} requested ${d.type} on ${d.date}`,
                    date: d.createdAt?.toDate() || new Date(),
                    route: 'office'
                };
            });
            updateTasks();
        }));

        // 5. Professional Development (PD)
        const qPD = query(
            collection(db, `artifacts/${appId}/pd_activities`),
            where('managerId', '==', uid),
            where('status', 'in', ['Pending Approval', 'Pending Verification'])
        );
        unsubscribes.push(onSnapshot(qPD, (snap) => {
            taskStreams.pd = snap.docs.map(doc => {
                const d = doc.data();
                const isProof = d.status === 'Pending Verification';
                return {
                    id: doc.id, type: 'pd', module: 'pd',
                    title: isProof ? `Verify Training Proof` : `Training Request`,
                    description: `${d.userName} - ${d.title}`,
                    date: d.createdAt?.toDate() || new Date(),
                    route: 'pd'
                };
            });
            updateTasks();
        }));

        // 6. Performance Reviews
        const qPerf = query(
            collection(db, `artifacts/${appId}/performance_reviews`),
            where('managerId', '==', uid),
            where('status', '==', 'Submitted')
        );
        unsubscribes.push(onSnapshot(qPerf, (snap) => {
            taskStreams.perf = snap.docs.map(doc => {
                const d = doc.data();
                return {
                    id: doc.id, type: 'performance', module: 'performance',
                    title: `Performance Review Submitted`,
                    description: `${d.userName} is ready for your assessment (${d.cycle})`,
                    date: d.updatedAt?.toDate() || new Date(),
                    route: 'performance'
                };
            });
            updateTasks();
        }));

        // 7. IT Center (Only if user is IT)
        if (isIT) {
            const qIT = query(
                collection(db, `artifacts/${appId}/it_requests`),
                where('status', 'in', ['Open', 'In Progress']) // Assuming these mean action required
            );
            unsubscribes.push(onSnapshot(qIT, (snap) => {
                taskStreams.it = snap.docs.map(doc => {
                    const d = doc.data();
                    return {
                        id: doc.id, type: 'IT', module: 'it',
                        title: `IT Ticket: ${d.subject}`,
                        description: `From ${d.requesterName} - Status: ${d.status}`,
                        date: d.createdAt?.toDate() || new Date(),
                        route: 'it'
                    };
                });
                updateTasks();
            }));
        }
        // 8. Pending User Registrations (Only if user is Admin)
        if (userProfile.role === 'Admin') {
            const qUsers = query(
                collection(db, `artifacts/${appId}/users`),
                where('status', '==', 'Pending')
            );
            unsubscribes.push(onSnapshot(qUsers, (snap) => {
                taskStreams.users = snap.docs.map(doc => {
                    const d = doc.data();
                    return {
                        id: doc.id, 
                        type: 'admin', 
                        module: 'user_approval',
                        title: `New User Registration`,
                        description: `${d.name} (${d.email}) is awaiting approval.`,
                        // Use createdAt if available, otherwise current date
                        date: d.createdAt?.toDate() || new Date(), 
                        route: 'admin' // Assumes your user management is in the 'admin' view
                    };
                });
                updateTasks();
            }));
        }
        setLoading(false);

	// 9. Exit Management Clearances
        const qExits = query(collection(db, `artifacts/${appId}/users`), where('status', '==', 'Approved'));
        unsubscribes.push(onSnapshot(qExits, (snap) => {
            const exitTasks = [];

            const createExitTask = (docId, u, deptName) => ({
                id: `exit_${docId}_${deptName.replace(/\s+/g, '')}`,
                type: 'HR',
                module: 'exit_management',
                title: `Exit Clearance: ${deptName}`,
                description: `${u.name} is offboarding. Sign-off is required.`,
                date: u.lastWorkingDate?.toDate() || new Date(),
                route: 'hr'
            });

            snap.docs.forEach(doc => {
                const u = doc.data();
                
                // FIX: Check for lastWorkingDate ONLY, and provide a fallback for legacy exits
                if (u.lastWorkingDate) {
                    const c = u.exitClearance || {
                        manager: { status: 'Pending' },
                        it: { status: 'Pending' },
                        finance: { status: 'Pending' },
                        hr: { status: 'Pending' }
                    };

                    // Create independent tasks for every role this user holds
                    if (u.managerId === uid && c.manager?.status !== 'Cleared') {
                        exitTasks.push(createExitTask(doc.id, u, 'Reporting Manager'));
                    }
                    if (isIT && c.it?.status !== 'Cleared') {
                        exitTasks.push(createExitTask(doc.id, u, 'IT Department'));
                    }
                    if (isFinanceApprover && c.finance?.status !== 'Cleared') {
                        exitTasks.push(createExitTask(doc.id, u, 'Finance'));
                    }
                    if (isHR && c.hr?.status !== 'Cleared') {
                        exitTasks.push(createExitTask(doc.id, u, 'HR'));
                    }
                }
            });
            taskStreams.exits = exitTasks;
            updateTasks();
        }));

        // Cleanup function
        return () => {
            unsubscribes.forEach(unsub => unsub());
        };

    }, [appId, userProfile]);

    return { tasks, loading };
}